import * as React from 'react';
import { observer } from 'mobx-react-lite';
import TimeSuggestionsHost from 'owa-calendar-meeting-forms-common-view/lib/components/TimeSuggestionsHost';
import TimeRow from './TimeRow';
import { findMeetingTimesConstants } from 'owa-calendar-time-suggestions/lib/constants';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import type { IButtonStyles } from '@fluentui/react/lib/Button';
import type { ColorScheme } from 'owa-color-utils/lib/ColorScheme';
import type { ITheme } from '@fluentui/style-utilities';
import { isFeatureEnabled } from 'owa-feature-flags';
import shouldShowTimeSuggestions from 'owa-calendar-compose-form-store/lib/selectors/shouldShowTimeSuggestions';
import type { OwaDate } from 'owa-datetime';
import { getIsNarrowFullCompose } from '../selectors/getIsNarrowFullCompose';

let styles = require('./FullComposeLeftPane.scss');

export interface TimeComponentProps {
    event: CalendarEvent;
    calendarColorScheme: ColorScheme;
    onTimeSuggestionClicked: (start: OwaDate, end: OwaDate) => void;
    onTimeSuggestionNotFound: () => void;
    calendarTheme: ITheme;
    calendarThemedButtonStyle: IButtonStyles;
    isNarrow?: boolean;
    formId?: string | null;
}

/* This class will be removed after TimeSuggestionsExperiments are done */
export default observer(function TimeComponent(props: TimeComponentProps) {
    const isExp =
        isFeatureEnabled('cal-mf-timeSuggestions-exp-a') ||
        isFeatureEnabled('cal-mf-timeAccommodations');
    const isAllDayEvent = props.event?.IsAllDayEvent;
    const showTimeSuggestions = shouldShowTimeSuggestions() && !isAllDayEvent;
    const isNarrowFC = getIsNarrowFullCompose();

    /** Splitting this logic instead of using flex-direction: column-reverse for accessibility reasons
     * There's a keyboard navigating disconnect between the DOM and flex visual presentation when using row-reverse and column-reverse
     * https://tink.uk/flexbox-the-keyboard-navigation-disconnect/ */
    if (isExp) {
        return (
            <div>
                {showTimeSuggestions && <TimeSuggestionHost {...props} isNarrow={isNarrowFC} />}
                {<DateTimeRow {...props} />}
            </div>
        );
    } else {
        return (
            <div>
                {<DateTimeRow {...props} />}
                {showTimeSuggestions && <TimeSuggestionHost {...props} isNarrow={isNarrowFC} />}
            </div>
        );
    }
});

export const TimeSuggestionHost = React.forwardRef(function TimeSuggestionHost(
    props: TimeComponentProps
) {
    const { event, calendarColorScheme, onTimeSuggestionClicked, onTimeSuggestionNotFound } = props;

    return (
        <TimeSuggestionsHost
            className={styles.timeSuggestions}
            colorScheme={calendarColorScheme}
            event={event}
            maxCandidates={findMeetingTimesConstants.MAX_CANDIDATES}
            onTimeSuggestionClicked={onTimeSuggestionClicked}
            onTimeSuggestionNotFound={onTimeSuggestionNotFound}
        />
    );
});

function DateTimeRow(props: TimeComponentProps) {
    const { event, calendarTheme, calendarThemedButtonStyle, calendarColorScheme, formId } = props;
    return (
        <TimeRow
            event={event}
            calendarTheme={calendarTheme}
            buttonStyle={calendarThemedButtonStyle}
            calendarColorScheme={calendarColorScheme}
            formId={formId}
        />
    );
}
